import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import {
  getAuthorListAsyncForTypeahead,
  getRubricListAsyncForTypeahead,
  getFilteredPrimarySectionsForDropdown,
  getTagListAsyncForTypeahead,
  getFilteredTagsForTypeahead,
  getFilteredPrimaryTagsForDropdown,
  getTopicListAsyncForTypeahead,
  getSubtopicListAsyncForTypeahead,
} from "editor/actions/metadata";
import { TEMPLATES } from "config/constants/editor";
import { onChange, validateFormAsync } from "editor/actions/form";

import Typeahead from "components/library/Typeahead";
import AsyncDropdown from "components/library/AsyncDropdown";
import Label from "components/library/Label";
import InputGroup from "editor/components/InputGroup";

import styles from "./Metadata.css";

class Metadata extends React.Component {
  constructor(props) {
    super(props);
    this.getFilteredSubtopics = this._getFilteredSubtopics.bind(this);
    this.getFilteredFeaturedSubtopics = this._getFilteredFeaturedSubtopics.bind(
      this
    );
    this.onChangeSubtopics = this._onChangeSubtopics.bind(this);
    this.onChangeTopics = this._onChangeTopics.bind(this);
    this.idPrefix = Math.random()
      .toString(36)
      .substring(7);
  }

  getId(field) {
    return `${this.idPrefix}-${field}`;
  }

  _getFilteredSubtopics(options, _, currentValues) {
    if (options) {
      // Get the id of already selected topics
      const selectedTopicIds = this.props.form.topics.map(topic => topic.id);
      // Get the id of already selected subtopics
      const currentValueIds = currentValues.map(
        currentValue => currentValue.id
      );

      return options.filter(subtopic => {
        return (
          // Only show subtopics related to already selected topics
          selectedTopicIds.includes(subtopic.topic) &&
          // Don't show subtopics that were already selected
          !currentValueIds.includes(subtopic.id)
        );
      });
    }
    return [];
  }

  _getFilteredFeaturedSubtopics(options, _, currentValues) {
    if (options) {
      // Get the id of already selected topics
      const selectedSubtopicIds = this.props.form.subtopics.map(
        subtopic => subtopic.id
      );
      // Get the id of already selected featured subtopics
      const currentValueIds = currentValues.map(
        currentValue => currentValue.id
      );

      return options.filter(featuredSubtopic => {
        return (
          // Only allow featuring for already selected subtopics
          selectedSubtopicIds.includes(featuredSubtopic.id) &&
          // Don't show subtopics that were already featured
          !currentValueIds.includes(featuredSubtopic.id)
        );
      });
    }
    return [];
  }

  _onChangeSubtopics(newSubtopics) {
    this.props.onChange("subtopics")(newSubtopics);
    // if we've removed a subtopic, remove the featured relation
    const newFeaturedSubtopics = this.props.form.featured_subtopics.filter(
      featuredSubtopic =>
        newSubtopics.some(subtopic => featuredSubtopic.id === subtopic.id)
    );
    this.props.onChange("featured_subtopics")(newFeaturedSubtopics);
  }

  _onChangeTopics(newTopics) {
    this.props.onChange("topics")(newTopics);
    // if we've removed a topic, remove the associated subtopics
    const newSubtopics = this.props.form.subtopics.filter(subtopic =>
      newTopics.some(topic => subtopic.topic === topic.id)
    );
    this.onChangeSubtopics(newSubtopics);
  }

  render() {
    return (
      <div className={styles.root}>
        <InputGroup
          headerText="Info & Options"
          inputs={[
            <>
              <Label htmlFor={this.getId("authors")}>Author(s)</Label>
              <Typeahead
                className="gtm-add-author"
                inputId={this.getId("authors")}
                value={this.props.form.authors}
                onChange={this.props.onChange("authors")}
                loadOptions={this.props.getAuthorListAsyncForTypeahead}
                labelKey="display_name"
                valueKey="id"
                placeholder="Add an author"
                disabled={this.props.status.isReadOnly}
                error={this.props.form.errors.authors}
              />
            </>,
            <>
              <Label htmlFor={this.getId("editors")}>Editor(s)</Label>
              <Typeahead
                className="gtm-add-editor"
                inputId={this.getId("editors")}
                value={this.props.form.editors}
                onChange={this.props.onChange("editors")}
                loadOptions={this.props.getAuthorListAsyncForTypeahead}
                labelKey="display_name"
                valueKey="id"
                placeholder="Add an editor"
                disabled={this.props.status.isReadOnly}
              />
            </>,
            <>
              <Label htmlFor={this.getId("rubric")}>Text over headline</Label>
              <AsyncDropdown
                className="gtm-change-rubric"
                inputId={this.getId("rubric")}
                value={this.props.form.rubric}
                onChange={this.props.onChange("rubric")}
                loadOptions={this.props.getRubricListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                placeholder="Select a rubric"
                disabled={
                  this.props.status.isReadOnly ||
                  this.props.form.template !== TEMPLATES.DEFAULT
                }
                backspaceRemoves={false}
              />
            </>,
          ]}
        />

        <InputGroup
          headerText="Tags"
          inputs={[
            <>
              <Label htmlFor={this.getId("primary_tag")}>Primary tag*</Label>
              <AsyncDropdown
                className="gtm-add-primary-tag"
                inputId={this.getId("primary_tag")}
                value={this.props.form.primary_tag}
                onChange={this.props.onChange("primary_tag")}
                loadOptions={this.props.getTagListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                filterOptions={this.props.getFilteredPrimaryTagsForDropdown}
                placeholder="Select a primary tag"
                disabled={this.props.status.isReadOnly}
                error={this.props.form.errors.primaryTag}
                data-cy="primaryTag"
              />
            </>,
            <>
              <Label htmlFor={this.getId("tags")}>Additional tags</Label>
              <Typeahead
                className="gtm-add-additional-tags"
                inputId={this.getId("tags")}
                value={this.props.form.tags}
                multi={true}
                onChange={this.props.onChange("tags")}
                loadOptions={this.props.getTagListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                autoload={false}
                removeSelected={true}
                filterOptions={this.props.getFilteredTagsForTypeahead}
                placeholder="Add additional tags"
                disabled={this.props.status.isReadOnly}
              />
            </>,
          ]}
        />

        <InputGroup
          headerText="Sections & Subsections"
          inputs={[
            <>
              <Label htmlFor={this.getId("topics")}>Section(s)</Label>
              <Typeahead
                className="gtm-add-topics"
                inputId={this.getId("topics")}
                value={this.props.form.topics}
                onChange={this.onChangeTopics}
                loadOptions={this.props.getTopicListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                disabled={this.props.status.isReadOnly}
                placeholder="Select section(s)"
                error={this.props.form.errors.topics}
                renderArrow={true}
                onBlur={() =>
                  this.props
                    .validateFormAsync(this.props.form)
                    .catch(error => void error)
                }
              />
            </>,
            <>
              <Label htmlFor={this.getId("subtopics")}>Subsection(s)</Label>
              <Typeahead
                className="gtm-add-subtopics"
                inputId={this.getId("subtopics")}
                value={this.props.form.subtopics}
                onChange={this.onChangeSubtopics}
                loadOptions={this.props.getSubtopicListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                filterOptions={this.getFilteredSubtopics}
                disabled={
                  this.props.status.isReadOnly ||
                  (this.props.form.topics &&
                    this.props.form.topics.length === 0)
                }
                placeholder={
                  this.props.form.topics && this.props.form.topics.length > 0
                    ? "Select a subsection"
                    : "Select a section before adding subsection(s)"
                }
                error={this.props.form.errors.subtopics}
                renderArrow={true}
              />
            </>,
            <>
              <Label htmlFor={this.getId("featured_subtopics")}>
                Featured for subsection(s)
              </Label>
              <Typeahead
                className="gtm-add-featured-subtopics"
                inputId={this.getId("featured_subtopics")}
                value={this.props.form.featured_subtopics}
                onChange={this.props.onChange("featured_subtopics")}
                loadOptions={this.props.getSubtopicListAsyncForTypeahead}
                labelKey="name"
                valueKey="id"
                filterOptions={this.getFilteredFeaturedSubtopics}
                disabled={
                  this.props.status.isReadOnly ||
                  (this.props.form.subtopics &&
                    this.props.form.subtopics.length === 0)
                }
                placeholder={
                  this.props.form.subtopics &&
                  this.props.form.subtopics.length > 0
                    ? "Add subsections to feature in"
                    : "Select a subsection for this story first"
                }
                error={this.props.form.errors.subtopics}
                renderArrow={true}
              />
            </>,
          ]}
        />
      </div>
    );
  }
}

Metadata.propTypes = {
  validateFormAsync: PropTypes.func.isRequired,
  getBaseUrl: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  getAuthorListAsyncForTypeahead: PropTypes.func.isRequired,
  getRubricListAsyncForTypeahead: PropTypes.func.isRequired,
  getTagListAsyncForTypeahead: PropTypes.func.isRequired,
  getFilteredTagsForTypeahead: PropTypes.func.isRequired,
  getFilteredPrimaryTagsForDropdown: PropTypes.func.isRequired,
  getTopicListAsyncForTypeahead: PropTypes.func.isRequired,
  getSubtopicListAsyncForTypeahead: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  status: PropTypes.object.isRequired,
  id: PropTypes.string,
};

const mapStateToProps = state => ({
  form: state.editor.form,
  status: state.editor.status,
});

const mapDispatchToProps = {
  validateFormAsync,
  getAuthorListAsyncForTypeahead,
  getRubricListAsyncForTypeahead,
  getFilteredPrimarySectionsForDropdown,
  getTagListAsyncForTypeahead,
  getFilteredTagsForTypeahead,
  getFilteredPrimaryTagsForDropdown,
  onChange,
  getSubtopicListAsyncForTypeahead,
  getTopicListAsyncForTypeahead,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Metadata);
